import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Mail, CheckCircle2 } from "lucide-react";

export default function PrivateGate() {
  const { deploymentId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [admitted, setAdmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const redeem = useCallback(async (t) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/hosting/gate/${deploymentId}/redeem`, { token: t });
      setAdmitted(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  }, [deploymentId]);

  useEffect(() => {
    api.get(`/hosting/gate/${deploymentId}`).then((r) => setInfo(r.data)).catch(() => setInfo({ notfound: true }));
    if (token) redeem(token);
  }, [deploymentId, token, redeem]);

  const requestLink = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/hosting/gate/${deploymentId}/request-link`, { email: email.trim() });
      setSent(true);
      toast.success(data.message || "If that email has access, a link has been sent.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const wrap = { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--canvas)" };
  const card = { maxWidth: 440, width: "100%", padding: 32 };

  if (admitted) {
    return (
      <div style={wrap}>
        <div className="k-container" style={card} data-testid="private-gate-admitted">
          <CheckCircle2 size={28} className="k-tag-green" aria-hidden />
          <h1 className="k-heading" style={{ fontSize: 22, marginTop: 12, marginBottom: 6 }}>You're in</h1>
          <p className="k-muted" style={{ fontSize: 14 }}>
            Access granted for <strong style={{ color: "var(--heading)" }}>{admitted.email}</strong>. {admitted.note}
          </p>
          <p className="k-muted" style={{ fontSize: 12, marginTop: 14 }}>
            Live serving of the app runs on the managed-hosting adapter, which is in sandbox until managed hosting is cleared for live traffic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div className="k-container" style={card} data-testid="private-gate">
        <Lock size={26} className="k-muted" aria-hidden />
        <h1 className="k-heading" style={{ fontSize: 22, marginTop: 12, marginBottom: 6 }} data-testid="private-gate-title">
          {info?.notfound ? "App not found" : "This app is private"}
        </h1>
        {info?.notfound ? (
          <p className="k-muted" style={{ fontSize: 14 }}>We couldn't find this app.</p>
        ) : info && !info.is_private ? (
          <p className="k-muted" style={{ fontSize: 14 }} data-testid="private-gate-open">
            This app isn't private right now.
          </p>
        ) : (
          <>
            <p className="k-muted" style={{ fontSize: 14, marginBottom: 18 }}>
              {info?.message || "This app is invite-only. Enter your email to receive a one-time access link. If you've been invited, or your email domain is allowed, you'll be let straight in."}
            </p>
            {sent ? (
              <div className="k-tag-green" data-testid="private-gate-sent" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <Mail size={14} aria-hidden /> If that email has access, a one-time link is on its way.
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input className="k-control" data-testid="private-gate-email" type="email" placeholder="you@company.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
                <button className="k-btn k-btn-primary" data-testid="private-gate-request" disabled={busy || !email.trim()} onClick={requestLink}>
                  {busy ? "Sending…" : "Send my link"}
                </button>
              </div>
            )}
            <p className="k-muted" style={{ fontSize: 12, marginTop: 18 }}>
              Think you should have access? Ask the app owner to invite your email or allow your domain.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
